import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { getAllStaff, createStaffUser, deleteUser } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
    Select, 
    SelectContent, 
    SelectItem, 
    SelectTrigger, 
    SelectValue 
} from '../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    DialogFooter,
} from '../components/ui/dialog';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { 
    ArrowLeft, 
    Users, 
    Plus, 
    Trash2, 
    Shield, 
    User,
    Loader2,
    AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDate } from '../lib/utils';

export default function StaffManagementPage() {
    const { user, isAdmin } = useAuth();
    const navigate = useNavigate();
    const [staff, setStaff] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        nome: '',
        cognome: '',
        email: '',
        password: '',
        ruolo: 'operatore'
    });

    useEffect(() => {
        if (!isAdmin()) {
            navigate('/operator');
            return;
        }
        loadStaff();
    }, []);

    const loadStaff = async () => {
        try {
            const response = await getAllStaff();
            setStaff(response.data);
        } catch (error) {
            console.error('Error loading staff:', error);
            toast.error('Errore nel caricamento degli utenti');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);

        try {
            await createStaffUser(formData);
            toast.success('Utente creato con successo!');
            setIsDialogOpen(false);
            setFormData({
                nome: '',
                cognome: '',
                email: '',
                password: '',
                ruolo: 'operatore'
            });
            loadStaff();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Errore nella creazione dell\'utente');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteClick = (staffUser) => {
        setUserToDelete(staffUser);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!userToDelete) return;

        try {
            await deleteUser(userToDelete.id);
            toast.success('Utente eliminato');
            loadStaff();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione');
        } finally {
            setDeleteDialogOpen(false);
            setUserToDelete(null);
        }
    };

    return (
        <div className="min-h-screen bg-slate-50">
            {/* Header */}
            <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center h-16">
                        <Button 
                            variant="ghost" 
                            onClick={() => navigate('/operator')}
                            className="mr-4"
                        >
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            Indietro
                        </Button>
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-slate-900 flex items-center justify-center">
                                <Users className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <h1 className="text-lg font-bold text-slate-900">Gestione Staff</h1>
                                <p className="text-xs text-slate-500">Crea e gestisci operatori e admin</p>
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Actions */}
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h2 className="text-xl font-bold text-slate-900">Utenti Staff</h2>
                        <p className="text-sm text-slate-500">{staff.length} utenti registrati</p>
                    </div>
                    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                        <DialogTrigger asChild>
                            <Button className="bg-indigo-500 hover:bg-indigo-600" data-testid="add-staff-btn">
                                <Plus className="w-4 h-4 mr-2" />
                                Nuovo Utente
                            </Button>
                        </DialogTrigger>
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle>Crea Nuovo Utente Staff</DialogTitle>
                                <DialogDescription>
                                    Aggiungi un nuovo operatore o amministratore al sistema
                                </DialogDescription>
                            </DialogHeader>
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="nome">Nome</Label>
                                        <Input
                                            id="nome"
                                            name="nome"
                                            value={formData.nome}
                                            onChange={handleChange}
                                            required
                                            data-testid="staff-nome-input"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="cognome">Cognome</Label>
                                        <Input
                                            id="cognome"
                                            name="cognome"
                                            value={formData.cognome}
                                            onChange={handleChange}
                                            required
                                            data-testid="staff-cognome-input"
                                        />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="email">Email</Label>
                                    <Input
                                        id="email"
                                        name="email"
                                        type="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        required
                                        data-testid="staff-email-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="password">Password</Label>
                                    <Input
                                        id="password"
                                        name="password"
                                        type="password"
                                        value={formData.password}
                                        onChange={handleChange}
                                        required
                                        minLength={6}
                                        data-testid="staff-password-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="ruolo">Ruolo</Label>
                                    <Select 
                                        value={formData.ruolo} 
                                        onValueChange={(value) => setFormData({...formData, ruolo: value})}
                                    >
                                        <SelectTrigger data-testid="staff-ruolo-select">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="operatore">
                                                <div className="flex items-center gap-2">
                                                    <User className="w-4 h-4" />
                                                    Operatore
                                                </div>
                                            </SelectItem>
                                            <SelectItem value="admin">
                                                <div className="flex items-center gap-2">
                                                    <Shield className="w-4 h-4" />
                                                    Amministratore
                                                </div>
                                            </SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <DialogFooter>
                                    <Button 
                                        type="button" 
                                        variant="outline" 
                                        onClick={() => setIsDialogOpen(false)}
                                    >
                                        Annulla
                                    </Button>
                                    <Button 
                                        type="submit" 
                                        disabled={submitting}
                                        className="bg-indigo-500 hover:bg-indigo-600"
                                        data-testid="staff-submit-btn"
                                    >
                                        {submitting ? (
                                            <>
                                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                Creazione...
                                            </>
                                        ) : (
                                            'Crea Utente'
                                        )}
                                    </Button>
                                </DialogFooter>
                            </form>
                        </DialogContent>
                    </Dialog>
                </div>

                {/* Staff Table */}
                <Card className="border-slate-200">
                    <CardContent className="p-0">
                        {loading ? (
                            <div className="text-center py-12">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500 mx-auto"></div>
                                <p className="mt-4 text-slate-500">Caricamento...</p>
                            </div>
                        ) : staff.length === 0 ? (
                            <div className="text-center py-12">
                                <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                                <h3 className="text-lg font-semibold text-slate-900 mb-2">Nessun utente staff</h3>
                                <p className="text-slate-500">Clicca "Nuovo Utente" per iniziare</p>
                            </div>
                        ) : (
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Nome</TableHead>
                                        <TableHead>Email</TableHead>
                                        <TableHead>Ruolo</TableHead>
                                        <TableHead>Creato il</TableHead>
                                        <TableHead className="text-right">Azioni</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {staff.map((staffUser) => (
                                        <TableRow key={staffUser.id} data-testid={`staff-row-${staffUser.id}`}>
                                            <TableCell>
                                                <div className="flex items-center gap-2">
                                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                                        staffUser.ruolo === 'admin' 
                                                            ? 'bg-amber-100 text-amber-700' 
                                                            : 'bg-indigo-100 text-indigo-700'
                                                    }`}>
                                                        {staffUser.ruolo === 'admin' ? (
                                                            <Shield className="w-4 h-4" />
                                                        ) : (
                                                            <User className="w-4 h-4" />
                                                        )}
                                                    </div>
                                                    <span className="font-medium">
                                                        {staffUser.nome} {staffUser.cognome}
                                                    </span>
                                                </div>
                                            </TableCell>
                                            <TableCell>{staffUser.email}</TableCell>
                                            <TableCell>
                                                <Badge 
                                                    variant="outline"
                                                    className={
                                                        staffUser.ruolo === 'admin' 
                                                            ? 'bg-amber-50 text-amber-700 border-amber-200' 
                                                            : 'bg-indigo-50 text-indigo-700 border-indigo-200'
                                                    }
                                                >
                                                    {staffUser.ruolo === 'admin' ? 'Amministratore' : 'Operatore'}
                                                </Badge>
                                            </TableCell>
                                            <TableCell className="text-slate-500">
                                                {formatDate(staffUser.created_at)}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                {staffUser.id !== user.id && (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleDeleteClick(staffUser)}
                                                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                                        data-testid={`delete-staff-${staffUser.id}`}
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </CardContent>
                </Card>

                {/* Delete Confirmation Dialog */}
                <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>Conferma Eliminazione</AlertDialogTitle>
                            <AlertDialogDescription>
                                Sei sicuro di voler eliminare l'utente <strong>{userToDelete?.nome} {userToDelete?.cognome}</strong>?
                                Questa azione non può essere annullata.
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel>Annulla</AlertDialogCancel>
                            <AlertDialogAction 
                                onClick={handleDeleteConfirm}
                                className="bg-red-600 hover:bg-red-700"
                            >
                                Elimina
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </main>
        </div>
    );
}
